import React from 'react'
import { useNavigate } from 'react-router-dom'
import InitialAvatar from 'shared/components/InitialAvatar/InitialAvatar'
import { ROUTES } from 'shared/config/routes'
import { fetchStudentApplications } from 'api/applications'
import { plainPreviewFromHtml } from 'pages/ApplicationDetailsPage/components/Essay/utils/tiptapContent'
import {
	dashboardPriorityBackState,
	getEssayTypeLabel,
	navigateToStudentProfileFromPriority,
} from '../../utils/priorityCardUi'
import { ReactComponent as DockIcon } from 'assets/icons/dock.svg'
import { ReactComponent as CityIcon } from 'assets/icons/city.svg'
import { ReactComponent as ClockIcon } from 'assets/icons/clock.svg'
import { ReactComponent as EyeIcon } from 'assets/icons/openedEye.svg'
import { ReactComponent as ExternalIcon } from 'assets/icons/open-external.svg'
import s from './EssayCard.module.scss'

const MS_IN_MIN = 1000 * 60
const MS_IN_HOUR = MS_IN_MIN * 60
const MS_IN_DAY = MS_IN_HOUR * 24

const formatEssayCardTime = value => {
	if (!value) return ''
	const date = new Date(value)
	if (Number.isNaN(date.getTime())) return ''
	const diffMs = Math.max(0, Date.now() - date.getTime())
	if (diffMs < MS_IN_HOUR) {
		const mins = Math.max(1, Math.floor(diffMs / MS_IN_MIN))
		return `${mins} minute${mins === 1 ? '' : 's'} ago`
	}
	if (diffMs < MS_IN_DAY) {
		const hours = Math.max(1, Math.floor(diffMs / MS_IN_HOUR))
		return `${hours} hour${hours === 1 ? '' : 's'} ago`
	}
	const days = Math.max(1, Math.floor(diffMs / MS_IN_DAY))
	if (days === 1) return 'Yesterday'
	if (days <= 7) return `${days} day${days === 1 ? '' : 's'} ago`
	return date.toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
	})
}

const EssayCard = ({ item, onReview, sourceTab = null }) => {
	const navigate = useNavigate()
	const essayType = getEssayTypeLabel(item?.scope)
	const scope = String(item?.scope || '').toLowerCase()
	const isCommonApp = scope === 'common_app'
	const universityName = String(item?.universityName || '').trim()
	const programName = String(item?.programName || '').trim()
	const showSchoolMeta =
		!isCommonApp &&
		universityName &&
		universityName.toLowerCase() !== 'common app'
	const schoolLine = [universityName, programName].filter(Boolean).join(' · ')
	const timeLabel = formatEssayCardTime(item?.submittedAt) || 'Recently'
	// API preview is a slice of content_text (often HTML with align styles).
	const preview = plainPreviewFromHtml(item?.previewText, 160)

	const appNavOptions = {
		state: dashboardPriorityBackState(sourceTab),
	}

	const handleOpenApplication = async e => {
		e.stopPropagation()
		const appId = item?.applicationId || item?.applicationTargetId
		if (appId) {
			navigate(
				`${ROUTES.APPLICATIONS}/${encodeURIComponent(appId)}`,
				appNavOptions,
			)
			return
		}
		if (!item?.studentId) return
		try {
			const res = await fetchStudentApplications(item.studentId)
			const apps = Array.isArray(res?.applications)
				? res.applications
				: Array.isArray(res?.data?.applications)
					? res.data.applications
					: []
			const firstId = apps[0]?.id || apps[0]?.applicationId || null
			if (firstId) {
				navigate(
					`${ROUTES.APPLICATIONS}/${encodeURIComponent(firstId)}`,
					appNavOptions,
				)
				return
			}
		} catch (error) {
			console.error(error)
		}
		navigateToStudentProfileFromPriority(
			navigate,
			item.studentId,
			sourceTab,
		)
	}

	const handleOpenStudent = e => {
		e.stopPropagation()
		navigateToStudentProfileFromPriority(
			navigate,
			item?.studentId,
			sourceTab,
		)
	}

	const handleReview = e => {
		e.stopPropagation()
		onReview?.()
	}

	const studentName = item?.studentName || 'Student'

	return (
		<article className={s.card}>
			<div className={s.header}>
				<div className={s.headerLead}>
					<div className={s.avatar}>
						<InitialAvatar
							size={40}
							name={studentName}
							studentId={item?.studentId}
							photoUrl={item?.studentAvatarUrl}
							avatarSource={
								item?.studentAvatarUrl ? 'photo' : undefined
							}
							onClick={handleOpenStudent}
						/>
					</div>
					<div className={s.identity}>
						<button
							type="button"
							className={s.name}
							onClick={handleOpenStudent}
						>
							{studentName}
						</button>
						<span className={s.typeRow}>
							<DockIcon />
							<span>{essayType}</span>
						</span>
						{showSchoolMeta && schoolLine ? (
							<span className={s.schoolRow}>
								<CityIcon />
								<span>{schoolLine}</span>
							</span>
						) : null}
					</div>
				</div>
				<span className={s.time}>
					<ClockIcon />
					<span>{timeLabel}</span>
				</span>
			</div>

			<div className={s.body}>
				<h3 className={s.title}>{item?.title || 'Essay'}</h3>
				{preview ? <p className={s.preview}>{preview}</p> : null}
			</div>

			<div className={s.footer}>
				<button type="button" className={s.openBtn} onClick={handleOpenApplication}>
					<ExternalIcon />
					Open Application
				</button>
				<button type="button" className={s.reviewBtn} onClick={handleReview}>
					<EyeIcon />
					Review Essay
				</button>
			</div>
		</article>
	)
}

export default EssayCard
